'use client';

import React, { useContext, useEffect, useRef } from 'react';
import { FocusScope } from 'react-aria';
import { useMinLoadingState } from 'components/DropZone';
import { AnimatePresence } from 'framer-motion';
import {
  GlobalErrorContext,
  type GlobalErrorContextType,
} from 'hooks/useGlobalErrorContext';
import { useContextSelector } from 'use-context-selector';
import { ToastContents } from './ToastAlertContents';
import { ToastAnimationDisplay } from './ToastAnimationDisplay';
import { ToastItemContext } from './ToastItemContext';
import { ToastLoading } from './ToastLoading';
import { type ToastAlertProps } from './ToastTypes';

export function ToastAlert({
  title,
  details,
  variant = 'neutral',
  onRequestClose,
  adornmentBefore,
  adornmentAfter,
  initialFocusRef,
  isLoading: isLoadingProp = false,
  size = 'default',
  ...other
}: ToastAlertProps) {
  const { duration } = useContext(ToastItemContext);
  const isLoading = useMinLoadingState(isLoadingProp, 750);
  const ref = useRef<HTMLDivElement>(null);
  const hasGlobalErrorState = useContextSelector(
    GlobalErrorContext,
    (state: GlobalErrorContextType) => state.hasGlobalErrorState
  );
  const hideForGlobalError = variant === 'error' && hasGlobalErrorState;
  useEffect(() => {
    initialFocusRef?.current?.focus();
  }, [initialFocusRef]);

  if (hideForGlobalError) return null;

  // if the toast is not dismissible, we want to ensure that the toast is focusable
  const autoFocus: boolean =
    (duration === null &&
      (Boolean(onRequestClose) || Boolean(adornmentAfter))) ||
    Boolean(initialFocusRef?.current);

  return (
    <ToastAnimationDisplay isLoading={isLoadingProp} size={size} toastRef={ref}>
      <AnimatePresence initial={false}>
        {isLoading ? (
          <ToastLoading
            key="loading"
            ref={ref}
            details={details}
            isLoading={isLoadingProp}
            title={title}
          />
        ) : (
          <FocusScope autoFocus={autoFocus} contain restoreFocus>
            <ToastContents
              key="content"
              ref={ref}
              adornmentAfter={adornmentAfter}
              adornmentBefore={adornmentBefore}
              details={details}
              isLoading={isLoading}
              onRequestClose={onRequestClose}
              size={size}
              title={title}
              variant={variant}
              {...other}
            />
          </FocusScope>
        )}
      </AnimatePresence>
    </ToastAnimationDisplay>
  );
}
